'use client';

import { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadDocxTemplate, fillDocument } from './MandateWorkflow/DocumentLoader';
import PlaceholderForm from './MandateWorkflow/PlaceholderForm';
import DocumentPreview from './MandateWorkflow/DocumentPreview';
import { useGetBrokerProfileCardDetailsQuery } from '@/redux/services/brokerService';
import { useCreateESignMutation, useGetPropertyMandateDocumentQuery } from '@/redux/services/propertyService';
import { showToast } from '@/shared/Toast';

/**
 * Step indicator component
 */
const StepIndicator = ({ currentStep, steps }) => {
  return (
    <div className="flex items-center justify-between mb-8 max-w-2xl mx-auto">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;

        return (
          <div key={stepNumber} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs transition-colors duration-200
                  ${isCompleted ? 'bg-green-500 text-white' : ''}
                  ${isActive ? 'bg-orange text-white ring-4 ring-orange/20' : ''}
                  ${!isActive && !isCompleted ? 'bg-gray-200 dark:bg-gray-700 text-gray-500' : ''}
                `}
              >
                {isCompleted ? <span className="material-symbols-outlined text-sm">check</span> : stepNumber}
              </div>
              <span
                className={`
                  mt-2 text-[10px] font-semibold uppercase tracking-wider
                  ${isActive ? 'text-orange' : 'text-gray-400 dark:text-gray-500'}
                `}
              >
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`
                  h-[2px] w-full mt-[-18px]
                  ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}
                `}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main MandateWorkflow component
 */
const MandateWorkflow = ({ propertyId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Document state
  const [docInstance, setDocInstance] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [placeholderGroups, setPlaceholderGroups] = useState([]);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [filledBuffer, setFilledBuffer] = useState(null);
  const [brokerSigner, setBrokerSigner] = useState(null);

  const steps = ['Load', 'Fill', 'Preview', 'Submit'];

  const { data: brokerDetailsData, isLoading: isBrokerDetailsLoading, error: brokerDetailsError } = useGetBrokerProfileCardDetailsQuery(undefined);
  // Fetch mandate template base64 for the property and load the document
  const { data: mandateDocData, error: templateError } = useGetPropertyMandateDocumentQuery(propertyId, { skip: !propertyId });

  const [createESign] = useCreateESignMutation();

  // Step 1: Load the document template when API data is available
  useEffect(() => {
    const loadTemplate = async () => {
      if (!mandateDocData) return;
      setIsLoading(true);
      setError(null);
      try {
        const base64 = mandateDocData?.fileContent || mandateDocData?.base64 || mandateDocData?.file || mandateDocData;
        const fileUrl = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${base64}`;
        const result = await loadDocxTemplate(fileUrl);
        setDocInstance(result.doc);
        setPlaceholders(result.placeholders);
        setPlaceholderGroups(result.placeholderGroups || []);

        if (result.placeholders.length === 0) {
          setError('No placeholders detected in the template. Please ensure placeholders are in ${NAME} format.');
          return;
        }

        const initialData = {};
        result.placeholders.forEach((placeholder) => {
          initialData[placeholder] = '';
        });
        setFormData(initialData);
        setCurrentStep(2);
      } catch (err) {
        setError(err?.message || 'Failed to load mandate template');
      } finally {
        setIsLoading(false);
      }
    };
    loadTemplate();
  }, [mandateDocData]);

  useEffect(() => {
    if (templateError) {
      setError(templateError?.data?.message || templateError?.message || 'Failed to fetch mandate template');
    }
  }, [templateError]);

  // Validate form data
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    const missingFields = [];

    // validate placeholders
    placeholders.forEach((placeholder) => {
      const value = formData[placeholder]?.trim();
      if (!value) {
        errors[placeholder] = 'Required';
        missingFields.push(placeholder);
        isValid = false;
      } else if (value.length > 100) {
        errors[placeholder] = 'Max 100 characters';
        isValid = false;
      }
    });

    // Validate Seller Details
    // Name
    const sellerName = formData.seller_name?.trim();
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!sellerName) {
      errors.seller_name = 'Required';
      isValid = false;
    } else if (!nameRegex.test(sellerName)) {
      errors.seller_name = 'Name can only contain letters and spaces';
      isValid = false;
    } else if (sellerName.length > 50) {
      errors.seller_name = 'Max 50 characters';
      isValid = false;
    }

    // Email
    const sellerEmail = formData.seller_email?.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!sellerEmail) {
      errors.seller_email = 'Required';
      isValid = false;
    } else if (!emailRegex.test(sellerEmail)) {
      errors.seller_email = 'Invalid email format';
      isValid = false;
    } else if (sellerEmail.length > 100) {
      errors.seller_email = 'Max 100 characters';
      isValid = false;
    }

    // Phone Number
    const sellerPhone = formData.seller_phone_number?.trim();
    const phoneRegex = /^\d{10}$/;
    if (!sellerPhone) {
      errors.seller_phone_number = 'Required';
      isValid = false;
    } else if (!phoneRegex.test(sellerPhone)) {
      errors.seller_phone_number = 'Phone number must be exactly 10 digits';
      isValid = false;
    }

    if (!isValid) {
      if (missingFields.length > 0) {
        showToast('error', 'Please fill all mandatory fields before previewing.');
      } else {
        showToast('error', 'Please Fix the errors before previewing.');
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle form data changes
  const handleFormChange = (newData) => {
    setFormData(newData);
    // Clear error for changed field
    const changedField = Object.keys(newData).find((key) => newData[key] !== formData[key]);
    if (changedField && formErrors[changedField]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[changedField];
        return updated;
      });
    }
  };

  // Step 2: Generate preview
  const handleGeneratePreview = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fill the document with form data
      const buffer = fillDocument(docInstance, formData, placeholderGroups);
      setFilledBuffer(buffer);
      setCurrentStep(3); // Move to preview step
    } catch (err) {
      setError(err.message);
      showToast('error', `Failed to generate preview: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Go back to edit

  // Step 4: Download filled document
  const handleDownloadDocument = () => {
    if (!filledBuffer) return;

    const blob = new Blob([filledBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    saveAs(blob, 'mandate_filled.docx');
  };

  const extractBrokerSigner = () => {
    const d = brokerDetailsData?.data || brokerDetailsData;
    const candidate = d;

    const name = (candidate?.fullName || '').trim();
    const email = (candidate?.email || '').trim();
    const phone = (candidate?.phoneNumber || '').toString().trim();

    return { name, email, phone };
  };

  useEffect(() => {
    if (!brokerDetailsData) return;
    if (brokerDetailsError) return;
    const signer = extractBrokerSigner();
    if (signer?.name || signer?.email || signer?.phone) {
      setBrokerSigner(signer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerDetailsData, brokerDetailsError]);

  const applyWordLikeStyling = (root) => {
    if (!root) return;

    Object.assign(root.style, {
      fontFamily: '"Times New Roman", "Nimbus Roman", serif',
      fontSize: '12pt',
      lineHeight: '1.45',
      color: '#000',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    });

    root.querySelectorAll('p').forEach((p) => {
      Object.assign(p.style, {
        margin: '0 0 8px',
        whiteSpace: 'pre-wrap',
      });
    });

    root.querySelectorAll('table').forEach((table) => {
      Object.assign(table.style, {
        width: '100%',
        borderCollapse: 'collapse',
        margin: '8px 0',
        tableLayout: 'fixed',
      });
    });

    root.querySelectorAll('td, th').forEach((cell) => {
      Object.assign(cell.style, {
        border: '1px solid #dcdcdc',
        padding: '6px 8px',
        verticalAlign: 'top',
        whiteSpace: 'pre-wrap',
      });
    });

    root.querySelectorAll('ul, ol').forEach((list) => {
      Object.assign(list.style, {
        margin: '0 0 8px 20px',
        padding: '0',
      });
    });
  };

  const convertDocxBufferToPdfBase64 = async (docxBuffer) => {
    const result = await mammoth.convertToHtml({ arrayBuffer: docxBuffer });
    const html = result?.value || '';

    const container = document.createElement('div');
    container.style.width = '794px';
    container.style.padding = '16px';
    container.innerHTML = html;
    applyWordLikeStyling(container);

    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();

    await pdf.html(container, {
      x: 20,
      y: 20,
      width: pageWidth - 40,
      autoPaging: 'text',
      windowWidth: 794,
      html2canvas,
    });

    const dataUri = pdf.output('datauristring');
    return dataUri.split(',')[1];
  };

  // Step 5: Submit to /e-sign/create
  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!propertyId) {
        throw new Error('Missing propertyId');
      }
      if (!filledBuffer) {
        throw new Error('Document is not ready yet. Please generate preview first.');
      }
      if (isBrokerDetailsLoading) {
        throw new Error('Fetching broker details. Please try again in a moment.');
      }
      if (brokerDetailsError) {
        throw new Error('Failed to fetch broker details');
      }

      const sellerName = (formData?.seller_name || '').trim();
      const sellerEmail = (formData?.seller_email || '').trim();
      const sellerPhone = (formData?.seller_phone_number || '').trim();

      const signer = brokerSigner || extractBrokerSigner();
      if (!signer?.name || !signer?.email || !signer?.phone) {
        throw new Error('Broker details are incomplete (name/email/phone)');
      }

      // Convert DOCX buffer to PDF base64
      let pdfBase64;
      try {
        pdfBase64 = await convertDocxBufferToPdfBase64(filledBuffer);
      } catch (e) {
        // Fallback: create a minimal PDF with text so API gets a PDF base64
        console.error('Error converting DOCX to PDF:', e);
        const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
        pdf.setFontSize(12);
        pdf.text('Mandate', 40, 60);
        pdf.text('Preview rendering failed. Please contact support.', 40, 90);
        const dataUri = pdf.output('datauristring');
        pdfBase64 = dataUri.split(',')[1];
      }

      if (!pdfBase64 || typeof pdfBase64 !== 'string') {
        throw new Error('Failed to generate PDF');
      }

      const payload = {
        title: 'Mandate',
        propertyId: propertyId,
        pdfFile: pdfBase64,
        fileName: 'mandate.pdf',
        signers: [
          {
            name: sellerName,
            email: sellerEmail,
            phone: sellerPhone,
            signingOrder: 1,
          },
          {
            name: signer.name,
            email: signer.email,
            phone: signer.phone,
            signingOrder: 2,
          },
        ],
        expiryDays: 30,
      };

      await createESign(payload).unwrap();

      showToast('success', 'Mandate sent for e-sign successfully!');

      setCurrentStep(4); // Move to success step
    } catch (err) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to submit document';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Render loading state
  if (currentStep === 1 && isLoading) {
    return (
      <div className="flex items-center justify-center p-20 bg-white dark:bg-gray-800 rounded-lg">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-orange border-t-transparent" />
          <span className="text-sm font-medium text-gray-500">Initializing template...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && currentStep === 1) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">Failed to Load Template</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-orange text-white rounded-lg hover:bg-orange/90">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-[#F2F2F2] dark:border-gray-700">
      <h2 className="text-lg font-semibold text-[#383838] dark:text-white mb-6 flex items-center gap-2">
        <span className="material-symbols-outlined text-orange">fact_check</span>
        Fill the Mandate Details
      </h2>

      <StepIndicator currentStep={currentStep} steps={steps} />

      {error && currentStep > 1 && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
          <p className="text-xs text-red-600 dark:text-red-400 font-medium">{error}</p>
        </div>
      )}

      {/* Step 2: Fill Form */}
      {currentStep === 2 && (
        <div>
          <PlaceholderForm placeholders={placeholders} formData={formData} onChange={handleFormChange} errors={formErrors} />

          <div className="mt-8 flex justify-end">
            <button type="button" onClick={handleGeneratePreview} disabled={isLoading} className="btn btn-primary btn-sm w-full sm:w-auto">
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                <span className="material-symbols-outlined btn-icon-sm mr-1">visibility</span>
              )}
              Preview
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview & Edit */}
      {currentStep === 3 && (
        <div>
          <DocumentPreview documentBuffer={filledBuffer} formData={formData} placeholders={placeholders} />

          <div className="mt-8 flex justify-end border-t border-[#F2F2F2] dark:border-gray-700 pt-6">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownloadDocument();
                }}
                className="btn btn-outline2 btn-md flex items-center gap-2 "
              >
                <span className="material-symbols-outlined icon me-1">download</span>
                Download
              </button>

              <button onClick={handleSubmit} disabled={isLoading} className="btn btn-secondary btn-md shadow-md active:scale-[0.98] border-none flex items-center gap-2">
                {isLoading ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : <span className="material-symbols-outlined icon me-1">send</span>}
                Submit Mandate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {currentStep === 4 && (
        <div className="text-center py-16 animate-in fade-in zoom-in duration-500">
          <div className="mx-auto w-20 h-20 bg-green-50 dark:bg-green-900/10 rounded-full flex items-center justify-center mb-6">
            <span className="material-symbols-outlined text-4xl text-green-500">task_alt</span>
          </div>
          <h3 className="text-xl font-bold text-[#383838] dark:text-white mb-2">Submission Successful!</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            Your mandate document has been processed. After you and the seller have signed the document sent to your respective emails, click the refresh button on the mandate document to update its
            status.
          </p>
        </div>
      )}
    </div>
  );
};

export default MandateWorkflow;
